import { forwardRef, useCallback, useEffect, useRef, useState, type PropsWithChildren } from "react"

import { Box, Fade, Slide, Stack, Typography } from "@mui/material"
import type { EmblaCarouselType } from "embla-carousel"
import useEmblaCarousel from "embla-carousel-react"
import { InView } from "react-intersection-observer"
import { Container, Grid, SvgIcon, type FC, type iconKeys } from ".."

export type BenefitsListItem = {
  title?: string
  subtitle: string
  icon: string
}

export type BenefitsListProps = {
  items: BenefitsListItem[]
}

const BenefitsList: FC<BenefitsListProps> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const scrollThumbRef = useRef<HTMLDivElement>(null)
  const [thumbWidth, setThumbWidth] = useState(20)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      // 1200px = hardcoded value from the design
      "(min-width: 1200px)": {
        active: false,
      },
    },
  })

  const onInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const snaps = emblaApi?.scrollSnapList()
      setScrollSnaps(snaps)

      if (containerRef.current && scrollTrackRef.current) {
        const { clientWidth, scrollWidth } = containerRef.current
        const { clientWidth: trackSize } = scrollTrackRef.current
        setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20))
      }
    },
    [containerRef, scrollTrackRef]
  )

  const onScroll = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (!scrollTrackRef.current || !scrollThumbRef.current) return

      const progress = emblaApi.scrollProgress()
      const { clientWidth: trackWidth } = scrollTrackRef.current
      const scrollX = progress * (trackWidth - thumbWidth)
      const thumb = scrollThumbRef.current
      thumb.style.left = `${scrollX}px`
    },
    [thumbWidth]
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const { current: trackCurrent } = scrollTrackRef
      const { current: contentCurrent } = containerRef

      if (!emblaApi || !trackCurrent || !contentCurrent) return

      const { clientX } = e
      const target = e.target as HTMLDivElement
      const rect = target.getBoundingClientRect()
      const trackLeft = rect.left
      const clickRatio = (clientX - trackLeft) / trackCurrent.clientWidth

      const closestSlide = scrollSnaps.reduce((prev, curr) => {
        return Math.abs(curr - clickRatio) < Math.abs(prev - clickRatio) ? curr : prev
      }, 0)
      const closestIndex = scrollSnaps.indexOf(closestSlide)
      emblaApi?.scrollTo(closestIndex)
    },
    [scrollSnaps, scrollTrackRef, containerRef]
  )

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    emblaApi.on("reInit", onInit)
    emblaApi.on("resize", onInit)
    emblaApi.on("scroll", onScroll)
  }, [emblaApi, containerRef, scrollTrackRef, onInit, onScroll])

  return (
    <Container className="component__benefits-list">
      <Box overflow="hidden" py={{ xs: 10, md: 24 }}>
        <Box>
          <BenefitsItem ref={containerRef}>
            {items?.map(({ title, subtitle, icon }, i) => (
              <InView key={title || subtitle || `benefit-${i}`} triggerOnce>
                {({ inView, ref }) => (
                  <Slide
                    direction="up"
                    ref={ref}
                    in={inView}
                    container={containerRef.current}
                    timeout={{ appear: 350, enter: 700 * (i + 1) }}
                  >
                    <Box>
                      <Fade in={inView} timeout={{ appear: 350, enter: 700 * i }}>
                        <Box
                          display="flex"
                          flexDirection={{ xs: "row", md: "column" }}
                          alignItems="center"
                          gap={{ xs: 4, md: 0 }}
                          maxWidth={{ xs: "100%", md: 300 }}
                        >
                          <BenefitsIcon icon={icon} />
                          <Stack
                            flexDirection="column"
                            alignItems={{ xs: "flex-start", md: "center" }}
                          >
                            {title && <BenefitsTitle title={title} />}
                            <BenefitsDescription description={subtitle} />
                          </Stack>
                        </Box>
                      </Fade>
                    </Box>
                  </Slide>
                )}
              </InView>
            ))}
          </BenefitsItem>
        </Box>
      </Box>
    </Container>
  )
}
export default BenefitsList

/** A sub-component for an individual Benefits List Item */
const BenefitsItem = forwardRef(({ children }: PropsWithChildren, ref) => (
  <Box
    ref={ref}
    display="flex"
    flexDirection={{ xs: "column", md: "row" }}
    gap={{ xs: 4, md: 16 }}
    alignItems={{ xs: "flex-start", md: "center" }}
    justifyContent="space-between"
    maxWidth={960}
    margin="0 auto"
  >
    {children}
  </Box>
))
BenefitsItem.displayName = "BenefitsItem"

/** A sub-component for the Icon in a Benefits List item */
const BenefitsIcon: FC<{ icon: string }> = ({ icon }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    width={{ xs: 64, md: 75 }}
    height={{ xs: 64, md: 75 }}
    bgcolor={(theme) => theme.palette["secondary.200"].main}
    borderRadius={9999}
    mb={{ xs: 0, md: 3 }}
  >
    <SvgIcon
      variant={(icon as iconKeys) || "distance"}
      size={{ xs: 24, md: 30 }}
      sx={{ fill: (theme) => theme.palette["secondary.800"].main }}
      viewBox={icon === "journeys" ? "0 0 30 30" : "0 0 24 24"}
    />
  </Stack>
)

/** A sub-component for the Title in a Benefits List Item */
const BenefitsTitle: FC<{ title: string }> = ({ title }) => (
  <Typography
    component="h2"
    variant="body.md"
    fontWeight={500}
    textAlign={{ xs: "left", md: "center" }}
  >
    {title}
  </Typography>
)

/** A sub-component for the Description in a Benefits List Item */
const BenefitsDescription: FC<{ description: string }> = ({ description }) => (
  <Typography
    component="h3"
    variant="body.md"
    sx={(theme) => ({
      color: theme.palette["neutral.700"].main,
      opacity: 0.8,
      fontSize: "13px",
      lineHeight: 1.6,
      textAlign: { xs: "left", md: "center" },
    })}
  >
    {description}
  </Typography>
)
